import React, { useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import DatePicker from 'react-datepicker'

import { Modal } from '../../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useGetWorkflowTemplatesQuery } from '../../../../store/integrations/integrations'
import { IntegrationServiceId } from '../../../../common/integrations'
import { pluralize } from '../../utils'
import { type CrmActionModalProps } from '../crmActionRegistry'

import { useResolvedResults } from './useResolvedResults'
import { useCrmEligibility } from './useCrmEligibility'
import { useCrmActionSave } from './useCrmActionSave'
import { CrmEligibilityNotice, CrmLoadingBody, CrmNoEligibleAlert } from './CrmEligibilityFeedback'
import { DueDateInput } from './DueDateInput'

export const WealthboxWorkflowActionModal: React.FC<CrmActionModalProps> = ({ open, onClose, resolveResults, tracking }) => {
  const accountId = useAppSelector(selectAccountId)
  const { results, resolving } = useResolvedResults(resolveResults)

  // A client can match with several results, but the workflow must start only once per client.
  const clientResults = useMemo(() => (results ? Array.from(new Map(results.map((result) => [result.clientId, result])).values()) : null), [results])
  const eligibility = useCrmEligibility(IntegrationServiceId.WEALTHBOX, clientResults ?? [])

  const { save, saving } = useCrmActionSave({
    tracking,
    eligibility,
    onClose,
    serviceId: IntegrationServiceId.WEALTHBOX,
    sourceObject: 'workflow',
    entityType: 'workflows',
    relatedToType: 'Contact',
    successToast: { title: 'Starting workflow', message: 'Wealthbox workflow start began.' },
    errorToast: { title: 'Failed to start workflow', message: 'Could not start the Wealthbox workflow. Please try again.' },
  })

  const { data, isFetching } = useGetWorkflowTemplatesQuery({ accountId, connector: IntegrationServiceId.WEALTHBOX })
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption | null>(null)
  const [label, setLabel] = useState('')
  const [startsAt, setStartsAt] = useState<Date | null>(() => new Date())

  const clientCount = clientResults?.length ?? null
  const header = clientCount === null ? 'Start workflow' : `Start workflow for ${clientCount} ${pluralize(clientCount, 'client')}`

  const templateOptions: SelectOption[] = (data?.data ?? []).map((template) => ({ id: template.id, label: template.name ?? 'Untitled workflow' }))

  const noEligibleClients = !resolving && eligibility.status === 'ready' && eligibility.eligibleCount === 0

  const body =
    resolving || eligibility.status === 'loading' ? (
      <CrmLoadingBody crmName='Wealthbox' />
    ) : noEligibleClients ? (
      <CrmNoEligibleAlert crmName='Wealthbox' />
    ) : (
      <div className='d-flex flex-column gap-4'>
        <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
        <Form.Group controlId='wealthboxWorkflowTemplate' className='d-flex flex-column'>
          <Form.Label className='bold'>
            Workflow template <span className='text-danger'>*</span>
          </Form.Label>
          <SelectDropdown
            className='py-2 zocks-px-0.75'
            options={templateOptions}
            initialSelectedOption={selectedTemplate}
            onOptionSelected={setSelectedTemplate}
            onClear={() => setSelectedTemplate(null)}
            placeholder='Select a workflow template'
            loading={isFetching}
            searchable
          />
        </Form.Group>
        <Form.Group controlId='wealthboxWorkflowLabel' className='d-flex flex-column'>
          <Form.Label className='bold'>Label</Form.Label>
          <Form.Control value={label} onChange={(e) => setLabel(e.target.value)} placeholder='Label…' disabled={saving} />
        </Form.Group>
        <Form.Group controlId='wealthboxWorkflowStartsAt' className='d-flex flex-column align-items-start'>
          <Form.Label className='bold'>Starts at</Form.Label>
          <DatePicker
            id='wealthboxWorkflowStartsAt'
            selected={startsAt}
            onChange={(date: Date | null) => setStartsAt(date)}
            todayButton='Today'
            showTimeSelect
            dateFormat='dd/MM/yyyy h:mm aa'
            disabled={saving}
            customInput={<DueDateInput fitContent />}
          />
        </Form.Group>
        <CrmEligibilityNotice eligibility={eligibility} verbPhrase='start a workflow for' crmName='Wealthbox' />
      </div>
    )

  const handleSave = async () => {
    if (!selectedTemplate) {
      return
    }
    // Wealthbox expects a numeric template id; the contact to link is added per client by the backend.
    const trimmedLabel = label.trim()
    await save({
      workflow_template: Number(selectedTemplate.id),
      ...(trimmedLabel ? { label: trimmedLabel } : {}),
      ...(startsAt ? { starts_at: startsAt.toISOString() } : {}),
    })
  }

  const primaryActionDisabled = noEligibleClients ? false : resolving || eligibility.status !== 'ready' || eligibility.eligibleCount === 0 || !selectedTemplate

  return (
    <Modal
      show={open}
      onHide={onClose}
      header={<h4>{header}</h4>}
      body={body}
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={noEligibleClients ? onClose : handleSave}
      footerPrimaryActionTitle={noEligibleClients ? 'Done' : 'Start workflow'}
      footerPrimaryActionDisabled={primaryActionDisabled}
      footerPrimaryActionLoading={saving}
      onFooterSecondaryAction={onClose}
      hideSecondaryAction={noEligibleClients}
      centered={true}
    />
  )
}
